/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */
import type { FeatureCollection, Point } from 'geojson';
import { handleResponse } from '@opengeoweb/api';
import type {
  Coverage,
  GridDomain,
  PointSeriesDomain,
  CoverageDomain,
} from '@opengeoweb/shared';
import { SessionStorageKey } from '@opengeoweb/shared';

const getStationDataBaseUrl = (): string =>
  (JSON.parse(sessionStorage.getItem(SessionStorageKey.CONFIG) ?? '{}')
    .GW_FLYSAFE_STATION_DATA_URL as string) || '';

const getSolarTerminatorBaseUrl = (): string =>
  (JSON.parse(sessionStorage.getItem(SessionStorageKey.CONFIG) ?? '{}')
    .GW_FLYSAFE_SOLAR_TERMINATOR_URL as string) || '';

// Wrap fetch + handleResponse so every failure mode (network error, non-OK
// HTTP status, parse error) is converted into a single Error with a
// descriptive message that identifies the URL involved. This keeps the
// call sites simple and makes console.error output actionable.
const fetchJson = async <T>(url: string): Promise<T> => {
  let response: Response;
  try {
    response = await fetch(url);
  } catch (err) {
    const cause = err instanceof Error ? err.message : String(err);
    throw new Error(`Network error fetching ${url}: ${cause}`);
  }

  try {
    return (await handleResponse(response)) as T;
  } catch (err) {
    const cause = err instanceof Error ? err.message : String(err);
    throw new Error(
      `Failed to fetch ${url} (status ${response.status}): ${cause}`,
    );
  }
};

const mergeCoverages = (
  coverages: Coverage<CoverageDomain>[],
): Coverage<CoverageDomain> => {
  if (!coverages.length) {
    throw new Error('No coverages to merge');
  }

  if (coverages.length === 1) {
    return coverages[0];
  }

  // sort by first timestamp
  coverages.sort(
    (
      coverageA: Coverage<CoverageDomain>,
      coverageB: Coverage<CoverageDomain>,
    ) => {
      const tA = coverageA.domain.axes.t?.values?.[0];
      const tB = coverageB.domain.axes.t?.values?.[0];

      const timeA = tA ? new Date(tA).getTime() : 0;
      const timeB = tB ? new Date(tB).getTime() : 0;

      return timeA - timeB;
    },
  );

  const merged: Coverage<CoverageDomain> = structuredClone(coverages[0]);

  for (let index = 1; index < coverages.length; index += 1) {
    const current = coverages[index];

    const target = merged.domain.axes.t?.values;
    const source = current.domain.axes.t?.values;

    if (target && source) {
      (target as (string | number)[]).push(...(source as (string | number)[]));
    }

    Object.keys(merged.ranges).forEach((key) => {
      (merged.ranges[key].values as number[]).push(
        ...((current.ranges[key]?.values ?? []) as number[]),
      );
    });
  }

  return merged;
};

// Await all promises; return only the fulfilled values.
// Rejections are logged via console.warn with the supplied label + index so
// it is clear which sub-request failed, but do not propagate. If every
// promise rejects, the result is an empty array; downstream code is
// expected to treat that as a hard error (mergeCoverages throws in that
// case).
const settleAll = async <T>(
  promises: Promise<T>[],
  label: string,
): Promise<T[]> => {
  const results = await Promise.allSettled(promises);
  const fulfilled: T[] = [];
  results.forEach((result, idx) => {
    if (result.status === 'fulfilled') {
      fulfilled.push(result.value);
    } else {
      console.warn(`${label} (#${idx}) failed:`, result.reason);
    }
  });
  return fulfilled;
};

const buildDatetimes = (baseDate: Date, days: number): string[] => {
  const datetimes: string[] = [];
  for (let idx = 0; idx < days; idx += 1) {
    const bd = new Date(baseDate);
    bd.setUTCDate(baseDate.getUTCDate() - idx);
    datetimes.push(`${bd.toISOString().slice(0, 19)}Z`);
  }
  return datetimes;
};

// Retrieve solar terminator measure (solar zenith angle) as CoverageJSON
const getCachedSZACoverageForLocation = async (
  station: string,
  datetime: string,
): Promise<Coverage<PointSeriesDomain>> => {
  const solarTerminatorBaseUrl = getSolarTerminatorBaseUrl();
  if (!solarTerminatorBaseUrl) {
    throw new Error('GW_FLYSAFE_SOLAR_TERMINATOR_URL not configured');
  }

  const url = new URL(
    `${solarTerminatorBaseUrl}/edr/solarterminator/${datetime}/${station}.covjson`,
  );

  return fetchJson<Coverage<PointSeriesDomain>>(url.toString());
};

// Retrieve solar terminator measure (solar zenith angle) as CoverageJSON
export const getCachedSZACoverageForPosition = async (
  coordinates: [number, number],
  days: number,
  requestedBaseDate?: Date,
): Promise<Coverage<PointSeriesDomain>> => {
  const [closestStation, closestDatetime] =
    await getClosestStation(coordinates);

  const latestAvailable = new Date(closestDatetime);
  const baseDate =
    requestedBaseDate && requestedBaseDate <= latestAvailable
      ? requestedBaseDate
      : latestAvailable;

  return getCachedSZACoverageById(closestStation, days, baseDate);
};

const centralAngle = (
  lon1: number,
  lat1: number,
  lon2: number,
  lat2: number,
): number => {
  return (
    Math.acos(
      Math.sin((Math.PI / 180) * lat1) * Math.sin((Math.PI / 180) * lat2) +
        Math.cos((Math.PI / 180) * lat1) *
          Math.cos((Math.PI / 180) * lat2) *
          Math.cos((Math.PI / 180) * (lon2 - lon1)),
    ) *
    (180 / Math.PI)
  );
};

const getClosestStation = async (
  coordinates: [number, number],
): Promise<[string, string]> => {
  const stationLocations = await getCachedStationLocations();
  let closestAngle = 360;
  let closestStation = '';
  let closestDatetime = '';

  stationLocations.features.forEach((feature) => {
    const angle = centralAngle(
      feature.geometry.coordinates[0],
      feature.geometry.coordinates[1],
      coordinates[0],
      coordinates[1],
    );
    if (angle < closestAngle) {
      closestAngle = angle;
      closestStation = String(feature.id);
      const props = feature.properties as Record<string, string>;
      closestDatetime = props.latest_timestamp
        .replace(' ', 'T')
        .replace('+00:00', 'Z');
    }
  });

  if (!closestStation) {
    throw new Error('No station found near the requested coordinates');
  }

  return [closestStation, closestDatetime];
};

// Retrieve station-related data as CoverageJSON for a given location (dens, ff, dd)
// All heights are retrieved
const getCachedStationCoverageForLocation = async (
  station: string,
  datetime: string,
): Promise<Coverage<GridDomain>> => {
  const stationDataBaseUrl = getStationDataBaseUrl();
  if (!stationDataBaseUrl) {
    throw new Error('GW_FLYSAFE_STATION_DATA_URL not configured');
  }

  const url = new URL(
    `${stationDataBaseUrl}/edr/vpts/${datetime}/${station}.covjson`,
  );

  return fetchJson<Coverage<GridDomain>>(url.toString());
};

// Retrieve station-related data as CoverageJSON for a given position (dens, ff, dd)
// All heights are retrieved
export const getCachedStationCoverageForPosition = async (
  coordinates: [number, number],
  days: number,
  requestedBaseDate?: Date,
): Promise<Coverage<GridDomain>> => {
  const [closestStation, closestDatetime] =
    await getClosestStation(coordinates);
  const latestAvailable = new Date(closestDatetime);
  const baseDate =
    requestedBaseDate && requestedBaseDate <= latestAvailable
      ? requestedBaseDate
      : latestAvailable;

  return getCachedStationCoverageById(closestStation, days, baseDate);
};

// Retrieve all station locations
export const getCachedStationLocations = async (): Promise<
  FeatureCollection<Point>
> => {
  const stationDataBaseUrl = getStationDataBaseUrl();
  if (!stationDataBaseUrl) {
    throw new Error('GW_FLYSAFE_STATION_DATA_URL not configured');
  }

  const url = new URL(`${stationDataBaseUrl}/edr/1.0.0/locations.geojson`);

  return fetchJson<FeatureCollection<Point>>(url.toString());
};

// Retrieve station coverage by stationId (fetches multiple days and merges).
// Individual day failures are tolerated: as long as at least one day
// succeeds, the merged result is returned. If all day-fetches fail,
// mergeCoverages will throw.
export const getCachedStationCoverageById = async (
  stationId: string,
  days: number,
  requestedBaseDate: Date,
): Promise<Coverage<GridDomain>> => {
  const datetimes = buildDatetimes(requestedBaseDate, days);

  const coverages = await settleAll(
    datetimes.map(async (dt) =>
      getCachedStationCoverageForLocation(stationId, dt),
    ),
    `getCachedStationCoverageById[${stationId}]`,
  );
  return mergeCoverages(coverages) as Coverage<GridDomain>;
};

// Retrieve SZA coverage by stationId (fetches multiple days and merges).
// Individual day failures are tolerated; see getCachedStationCoverageById.
export const getCachedSZACoverageById = async (
  stationId: string,
  days: number,
  requestedBaseDate: Date,
): Promise<Coverage<PointSeriesDomain>> => {
  const datetimes = buildDatetimes(requestedBaseDate, days);

  const coverages = await settleAll(
    datetimes.map(async (datetime) =>
      getCachedSZACoverageForLocation(stationId, datetime),
    ),
    `getCachedSZACoverageById[${stationId}]`,
  );
  return mergeCoverages(coverages) as Coverage<PointSeriesDomain>;
};

// Retrieve model data for a station.
// Individual day failures are tolerated; see getCachedStationCoverageById.
export const getCachedModelForStation = async (
  modelStationId: string,
  latestModelTimestamp: string,
): Promise<Coverage<PointSeriesDomain>> => {
  const stationDataBaseUrl = getStationDataBaseUrl();
  if (!stationDataBaseUrl) {
    throw new Error('GW_FLYSAFE_STATION_DATA_URL not configured');
  }

  const baseDate = new Date(
    latestModelTimestamp.replace(' ', 'T').replace('+00:00', 'Z'),
  );
  const days = 9;
  const datetimes = buildDatetimes(baseDate, days);

  const coverages = await settleAll(
    datetimes.map(async (datetime) => {
      const url = new URL(
        `${stationDataBaseUrl}/edr/model/${datetime}/${modelStationId}.covjson`,
      );
      return fetchJson<Coverage<PointSeriesDomain>>(url.toString());
    }),
    `getCachedModelForStation[${modelStationId}]`,
  );

  return mergeCoverages(coverages) as Coverage<PointSeriesDomain>;
};

// Retrieve SZA data for use with model data for a station.
// Individual day failures are tolerated; see getCachedStationCoverageById.
export const getCachedModelSZAForStation = async (
  modelStationId: string,
  latestModelTimestamp: string,
): Promise<Coverage<PointSeriesDomain>> => {
  const stationDataBaseUrl = getStationDataBaseUrl();
  if (!stationDataBaseUrl) {
    throw new Error('GW_FLYSAFE_STATION_DATA_URL not configured');
  }

  const baseDate = new Date(
    latestModelTimestamp.replace(' ', 'T').replace('+00:00', 'Z'),
  );
  const days = 9;
  const datetimes = buildDatetimes(baseDate, days);

  const coverages = await settleAll(
    datetimes.map(async (datetime) =>
      getCachedSZACoverageForLocation(modelStationId, datetime),
    ),
    `getCachedModelSZAForStation[${modelStationId}]`,
  );

  return mergeCoverages(coverages) as Coverage<PointSeriesDomain>;
};
